import config from '../config/environment';
import supabase from '../config/supabase';
import {
  parseCommercialGoalsDashboard,
  type CommercialGoalsDashboard,
} from '../utils/commercialGoalsDashboard';
import { handleServiceError } from '../utils/errorHandler';
import type { CommercialSalesGoalSnapshot } from '../utils/salesGoalCounter';

class CommercialSalesGoalServiceClass {
  async get(month: string): Promise<CommercialSalesGoalSnapshot> {
    const token = await this.getAccessToken();
    const url = new URL(`${config.supabaseFunctionsUrl}/commercial-sales-goals`);
    url.searchParams.set('month', month);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });

    return this.readSnapshot(response, 'carregar a meta de vendas');
  }

  async getDashboard(
    dateStart: string,
    dateEnd: string,
    goalMonth: string,
  ): Promise<CommercialGoalsDashboard> {
    const token = await this.getAccessToken();
    const url = new URL(`${config.supabaseFunctionsUrl}/commercial-sales-goals`);
    url.searchParams.set('dateStart', dateStart);
    url.searchParams.set('dateEnd', dateEnd);
    url.searchParams.set('goalMonth', goalMonth);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });
    const body = await response.json().catch(() => ({})) as { error?: string };

    if (!response.ok) {
      handleServiceError(
        new Error(body.error || 'Erro ao carregar o dashboard comercial'),
        'carregar o dashboard comercial',
      );
    }

    const snapshot = parseCommercialGoalsDashboard(body);
    if (!snapshot) {
      handleServiceError(
        new Error('Erro ao carregar o dashboard comercial'),
        'carregar o dashboard comercial',
      );
    }

    return snapshot;
  }

  async save(month: string, targetSales: number): Promise<CommercialSalesGoalSnapshot> {
    const token = await this.getAccessToken();
    const response = await fetch(`${config.supabaseFunctionsUrl}/commercial-sales-goals`, {
      method: 'POST',
      headers: this.buildHeaders(token),
      body: JSON.stringify({ month, targetSales }),
    });

    return this.readSnapshot(response, 'salvar a meta de vendas');
  }

  private async readSnapshot(response: Response, context: string): Promise<CommercialSalesGoalSnapshot> {
    const body = await response.json().catch(() => ({})) as Partial<CommercialSalesGoalSnapshot> & {
      error?: string;
    };

    if (!response.ok) {
      handleServiceError(new Error(body.error || `Erro ao ${context}`), context);
    }

    if (
      typeof body.month !== 'string'
      || typeof body.startsOn !== 'string'
      || typeof body.endsOn !== 'string'
      || typeof body.salesCount !== 'number'
      || (body.targetSales != null && typeof body.targetSales !== 'number')
    ) {
      handleServiceError(new Error(`Erro ao ${context}`), context);
    }

    return {
      month: body.month,
      startsOn: body.startsOn,
      endsOn: body.endsOn,
      salesCount: body.salesCount,
      targetSales: body.targetSales ?? null,
    };
  }

  private async getAccessToken(): Promise<string | undefined> {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token;
  }

  private buildHeaders(token: string | undefined): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: config.supabaseAnonKey,
    };
  }
}

export const CommercialSalesGoalService = new CommercialSalesGoalServiceClass();
